import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { TrendingUp, Users, Percent, Trophy } from "lucide-react";

const GOLD = "#FFD700";
const STATUS_COLORS = { approved: "#22C55E", pending: "#EAB308", rejected: "#EF4444" };
const STATUS_LABELS = { approved: "Godkända", pending: "Väntar", rejected: "Nekade" };

function formatDay(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("sv-SE", { day: "2-digit", month: "short" });
}

const tooltipStyle = {
  background: "#0A0A0A",
  border: "1px solid #FFD700",
  borderRadius: "2px",
  color: "#EDEDED",
  fontSize: "12px",
};

function ChartCard({ title, icon, children, testId, empty }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#222] p-4 md:p-6" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#FFD700]">{title}</h3>
      </div>
      {empty ? (
        <div className="h-[220px] flex items-center justify-center text-gray-600 text-sm">
          Ingen data ännu
        </div>
      ) : (
        children
      )}
    </div>
  );
}

function Kpi({ icon, label, value, suffix, testId }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#222] p-4 md:p-6" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs font-bold uppercase tracking-widest text-gray-500">{label}</span>
      </div>
      <p className="font-heading text-3xl text-white">
        {value}
        {suffix && <span className="text-lg text-[#FFD700] ml-1">{suffix}</span>}
      </p>
    </div>
  );
}

export default function AnalyticsDashboard({ analytics, isLoading }) {
  if (isLoading || !analytics) {
    return (
      <div className="bg-[#0A0A0A] border border-[#222] p-12 text-center text-gray-500">
        Laddar analys...
      </div>
    );
  }

  const dailyReferrals = (analytics.daily_referrals || []).map((d) => ({
    day: formatDay(d.date),
    count: d.count,
  }));
  const dailyApps = (analytics.daily_applications || []).map((d) => ({
    day: formatDay(d.date),
    count: d.count,
  }));
  const leaderboard = analytics.leaderboard || [];
  const sb = analytics.status_breakdown || {};
  const statusData = Object.keys(sb)
    .filter((k) => sb[k] > 0)
    .map((k) => ({ name: STATUS_LABELS[k], key: k, value: sb[k] }));

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Kpi
          icon={<TrendingUp className="w-4 h-4 text-[#FFD700]" />}
          label="Totalt biljettköp"
          value={analytics.total_referrals}
          testId="kpi-total-referrals"
        />
        <Kpi
          icon={<Users className="w-4 h-4 text-[#FFD700]" />}
          label="Snitt / ambassadör"
          value={analytics.avg_referrals_per_ambassador}
          testId="kpi-avg-referrals"
        />
        <Kpi
          icon={<Percent className="w-4 h-4 text-[#FFD700]" />}
          label="Godkännandegrad"
          value={analytics.approval_rate}
          suffix="%"
          testId="kpi-approval-rate"
        />
      </div>

      {/* Daily ticket sales */}
      <ChartCard
        title="Biljettförsäljning per dag"
        icon={<TrendingUp className="w-4 h-4 text-[#FFD700]" />}
        testId="chart-daily-sales"
        empty={dailyReferrals.length === 0}
      >
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={dailyReferrals} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={GOLD} stopOpacity={0.5} />
                <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
            <XAxis dataKey="day" stroke="#666" fontSize={11} tickLine={false} />
            <YAxis stroke="#666" fontSize={11} allowDecimals={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: GOLD, strokeOpacity: 0.2 }} />
            <Area
              type="monotone"
              dataKey="count"
              name="Biljettköp"
              stroke={GOLD}
              strokeWidth={2}
              fill="url(#goldFill)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leaderboard */}
        <ChartCard
          title="Topplista ambassadörer"
          icon={<Trophy className="w-4 h-4 text-[#FFD700]" />}
          testId="chart-leaderboard"
          empty={leaderboard.length === 0}
        >
          <ResponsiveContainer width="100%" height={Math.max(220, leaderboard.length * 38)}>
            <BarChart
              data={leaderboard}
              layout="vertical"
              margin={{ top: 0, right: 20, left: 10, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" horizontal={false} />
              <XAxis type="number" stroke="#666" fontSize={11} allowDecimals={false} tickLine={false} />
              <YAxis
                type="category"
                dataKey="name"
                stroke="#999"
                fontSize={11}
                width={90}
                tickLine={false}
              />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,215,0,0.06)" }} />
              <Bar dataKey="count" name="Biljettköp" fill={GOLD} radius={[0, 2, 2, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Status breakdown */}
        <ChartCard
          title="Ambassadörsstatus"
          icon={<Users className="w-4 h-4 text-[#FFD700]" />}
          testId="chart-status-breakdown"
          empty={statusData.length === 0}
        >
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={statusData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={3}
                stroke="none"
              >
                {statusData.map((entry) => (
                  <Cell key={entry.key} fill={STATUS_COLORS[entry.key]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-4 mt-2">
            {statusData.map((s) => (
              <div key={s.key} className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full inline-block"
                  style={{ background: STATUS_COLORS[s.key] }}
                />
                <span className="text-xs text-gray-400">
                  {s.name} <span className="text-white font-bold">{s.value}</span>
                </span>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Applications over time */}
      <ChartCard
        title="Ambassadörsansökningar per dag"
        icon={<Users className="w-4 h-4 text-[#FFD700]" />}
        testId="chart-applications"
        empty={dailyApps.length === 0}
      >
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={dailyApps} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
            <XAxis dataKey="day" stroke="#666" fontSize={11} tickLine={false} />
            <YAxis stroke="#666" fontSize={11} allowDecimals={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: GOLD, strokeOpacity: 0.2 }} />
            <Line
              type="monotone"
              dataKey="count"
              name="Ansökningar"
              stroke={GOLD}
              strokeWidth={2}
              dot={{ fill: GOLD, r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
